import React, { useState, useEffect } from 'react';
import { Download, Wifi, RefreshCw, X } from 'lucide-react';

/**
 * PWA Notifications Component
 * Handles 3 types of notifications:
 * 1. Install prompt - Suggests installing the app
 * 2. Offline ready - Confirms app works offline
 * 3. Update available - Alerts when new version is ready
 */
const PWANotifications = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showOfflineReady, setShowOfflineReady] = useState(false);
  const [showUpdateAvailable, setShowUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    // 1. Handle PWA Install Prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Hide install button if already installed
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setInstallPrompt(null);
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // 2. Listen for service worker messages (offline ready)
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === 'OFFLINE_READY') {
        setShowOfflineReady(true);
        // Auto-hide after 5 seconds
        setTimeout(() => setShowOfflineReady(false), 5000);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);

      // Also check if SW is already registered and show offline ready notification
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          // Show notification after a short delay
          setTimeout(() => {
            setShowOfflineReady(true);
            setTimeout(() => setShowOfflineReady(false), 5000);
          }, 1000);
        }
      });
    }

    // 3. Listen for service worker updates
    const handleSWUpdate = (event) => {
      const registration = event.detail;
      setWaitingWorker(registration.waiting);
      setShowUpdateAvailable(true);
    };

    window.addEventListener('swUpdated', handleSWUpdate);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('swUpdated', handleSWUpdate);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);

  // Handle install button click
  const handleInstallClick = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }

    setInstallPrompt(null);
  };

  // Handle update button click
  const handleUpdateClick = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return (
    <>
      {/* 1. Install App Notification */}
      {showInstallButton && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className="bg-white rounded-lg shadow-2xl p-4 max-w-sm border-2 border-green-500">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Download className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Installer l'application
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Accédez rapidement à votre gestionnaire d'index glycémique
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleInstallClick}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    Installer
                  </button>
                  <button
                    onClick={() => setShowInstallButton(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowInstallButton(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Offline Ready Notification */}
      {showOfflineReady && (
        <div className="fixed top-20 right-4 z-50 animate-slide-down">
          <div className="bg-green-600 text-white rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Wifi className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">
                  Application prête hors ligne ! 🎉
                </h3>
                <p className="text-sm text-green-100">
                  Vous pouvez maintenant utiliser l'app sans connexion internet
                </p>
              </div>
              <button
                onClick={() => setShowOfflineReady(false)}
                className="flex-shrink-0 text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Update Available Notification */}
      {showUpdateAvailable && waitingWorker && (
        <div className="fixed top-20 right-4 z-50 animate-slide-down">
          <div className="bg-blue-600 text-white rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <RefreshCw className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">
                  Nouvelle version disponible ! 🆕
                </h3>
                <p className="text-sm text-blue-100 mb-3">
                  Cliquez sur "Mettre à jour" pour profiter des dernières améliorations
                </p>
                <button
                  onClick={handleUpdateClick}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium transition-colors"
                >
                  Mettre à jour maintenant
                </button>
              </div>
              <button
                onClick={() => setShowUpdateAvailable(false)}
                className="flex-shrink-0 text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWANotifications;
